/** @module Starting a scan session: language, set hint, benchmark consent, camera or upload. */
import { useQuery } from "@tanstack/react-query";
import { Camera, Check, ChevronRight, ShieldCheck, Upload, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { api, problemFrom } from "@/api/client";
import { LocationSelect } from "@/components/location-select";
import { PHOTO_ACCEPT } from "@/features/scan/photo-files";
import { Switch } from "@/components/ui/controls";
import { LANGUAGES } from "@/lib/collection";
import { useSession } from "@/lib/use-session";

export interface SessionChoice {
  language: string;
  /** TCGdex set id; the recognition prefers its cards, e.g. while opening boosters. */
  setHint: string | null;
  setLabel: string | null;
  benchmark: boolean;
  /** Booked cards of this run go into this storage location. */
  location: string | null;
}

async function fetchSets() {
  const { data, response } = await api.GET("/api/v1/sets");
  if (!data) throw problemFrom(response);
  return data;
}

async function fetchMode() {
  const { data, response } = await api.GET("/api/v1/recognition/mode");
  if (!data) throw problemFrom(response);
  return data.mode;
}

/** "Deutsch", "English": the language in the reader's own words. */
export function languageName(code: string, locale: string): string {
  try {
    return new Intl.DisplayNames([locale], { type: "language" }).of(code) ?? code.toUpperCase();
  } catch {
    return code.toUpperCase();
  }
}

/** Newest sets first, filtered by name or printed code. */
function SetPicker({ onPick }: { onPick: (id: string | null, label: string | null) => void }) {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const sets = useQuery({ queryKey: ["sets"], queryFn: fetchSets, staleTime: 60 * 60_000 });
  const wanted = text.trim().toLocaleLowerCase();
  const shown = (sets.data ?? [])
    .filter(
      (set) =>
        !wanted ||
        set.name.toLocaleLowerCase().includes(wanted) ||
        (set.abbreviation ?? "").toLocaleLowerCase().includes(wanted),
    )
    .sort((a, b) => (b.release_date ?? "").localeCompare(a.release_date ?? ""))
    .slice(0, 40);
  return (
    <div className="flex flex-col gap-2">
      <input
        // A field opened for typing may take the focus.
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        className="input"
        value={text}
        placeholder={t("scan.session.setSearch")}
        aria-label={t("scan.session.setSearch")}
        onChange={(event) => {
          setText(event.target.value);
        }}
      />
      <ul className="flex max-h-72 flex-col overflow-y-auto">
        <li>
          <button
            type="button"
            className="menu-row w-full text-left"
            onClick={() => {
              onPick(null, null);
            }}
          >
            <X />
            <span className="grow">{t("scan.session.noSet")}</span>
          </button>
        </li>
        {shown.map((set) => (
          <li key={set.id}>
            <button
              type="button"
              className="menu-row w-full text-left"
              onClick={() => {
                onPick(set.id, `${set.name}${set.abbreviation ? ` · ${set.abbreviation}` : ""}`);
              }}
            >
              <span className="flex grow flex-col">
                <span>{set.name}</span>
                <span className="t-xs t-muted num">
                  {set.abbreviation ?? set.id}
                  {set.release_date ? ` · ${set.release_date.slice(0, 4)}` : ""}
                </span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function SessionForm({
  value,
  onChange,
  onCamera,
  onUpload,
}: {
  value: SessionChoice;
  onChange: (next: SessionChoice) => void;
  onCamera: () => void;
  /** Files picked here start the session with these settings. */
  onUpload: (files: File[]) => void;
}) {
  const { t, i18n } = useTranslation();
  const admin = useSession().account?.role === "admin";
  const locale = i18n.resolvedLanguage ?? "de";
  const [picking, setPicking] = useState(false);
  const mode = useQuery({ queryKey: ["recognition-mode"], queryFn: fetchMode, staleTime: 60_000 });

  return (
    <div className="flex flex-col gap-[18px]">
      <div className="flex flex-col gap-1">
        <h2 className="t-h2">{t("scan.session.title")}</h2>
        <p className="t-sm t-muted">{t("scan.session.hint")}</p>
      </div>
      <fieldset className="flex flex-col gap-2">
        <legend className="field-label pb-2">{t("scan.session.language")}</legend>
        <div className="flex flex-wrap gap-[6px]">
          {LANGUAGES.map((language) => (
            <button
              key={language}
              type="button"
              className="chip"
              aria-pressed={value.language === language}
              onClick={() => {
                onChange({ ...value, language });
              }}
            >
              {value.language === language ? <Check /> : null}
              {languageName(language, locale)}
            </button>
          ))}
        </div>
      </fieldset>
      <div className="field">
        <span className="field-label">
          {t("scan.session.set")}{" "}
          <span className="t-muted font-normal">{t("scan.session.optional")}</span>
        </span>
        {picking ? (
          <SetPicker
            onPick={(setHint, setLabel) => {
              onChange({ ...value, setHint, setLabel });
              setPicking(false);
            }}
          />
        ) : (
          <button
            type="button"
            className="input row-between text-left"
            onClick={() => {
              setPicking(true);
            }}
          >
            <span className={value.setLabel ? "" : "t-muted"}>
              {value.setLabel ?? t("scan.session.noSet")}
            </span>
            <ChevronRight className="size-4 text-(--text-muted)" />
          </button>
        )}
        <span className="field-hint">{t("scan.session.setHint")}</span>
      </div>
      <LocationSelect
        label={t("scan.session.location")}
        value={value.location}
        onChange={(location) => {
          onChange({ ...value, location });
        }}
      />
      {admin ? (
        <div className="row-between min-h-11">
          <span className="flex flex-col">
            <span>{t("scan.session.benchmark")}</span>
            <span className="t-xs t-muted">{t("scan.session.benchmarkHint")}</span>
          </span>
          <Switch
            checked={value.benchmark}
            label={t("scan.session.benchmark")}
            onChange={(benchmark) => {
              onChange({ ...value, benchmark });
            }}
          />
        </div>
      ) : (
        // Testers keep their photos (the server insists); they should know it.
        <p className="t-xs t-muted">{t("scan.session.photosKept")}</p>
      )}
      {mode.data === "calibration" ? (
        <div className="callout callout-accent">
          <ShieldCheck />
          <span>
            <b>{t("scan.session.calibrationTitle")}</b> {t("scan.session.calibration")}
          </span>
        </div>
      ) : null}
      <div className="flex flex-col gap-2">
        <button type="button" className="btn btn-primary btn-block" onClick={onCamera}>
          <Camera />
          {t("scan.startCamera")}
        </button>
        <label className="btn btn-secondary btn-block cursor-pointer">
          <Upload />
          {t("scan.session.upload")}
          <input
            type="file"
            accept={PHOTO_ACCEPT}
            multiple
            className="sr-only"
            onChange={(event) => {
              const files = Array.from(event.target.files ?? []);
              // Free the input so the same files can be picked again after a mistake.
              event.target.value = "";
              if (files.length > 0) onUpload(files);
            }}
          />
        </label>
      </div>
    </div>
  );
}
